import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, X, CreditCard, BookOpenCheck, Undo2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import { fm, withRid, STATUS_STYLES } from "@/components/payables/shared";
import { _RejectDialog } from "@/components/payables/RejectDialog";

export function BillsTab() {
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [coa, setCoa] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [populating, setPopulating] = useState(false);
  const [pay, setPay] = useState({ amount: "", date: format(new Date(), "yyyy-MM-dd"), method: "bank", memo: "", account_id: "" });
  const [reverseTarget, setReverseTarget] = useState(null);
  const [reverseReason, setReverseReason] = useState("");
  // iter 72ao — approval workflow. `approvalRequired` mirrors the entity
  // setting; `approvalFilter` toggles the top-of-list "Pending Approvals"
  // view. `rejectingBill` opens the rejection reason prompt.
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [approvalFilter, setApprovalFilter] = useState("all");
  const [rejectingBill, setRejectingBill] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const { isActive } = useCurrencyRail();
  // iter 76 — memoise filtered CoA list so the dropdown doesn't rebuild on
  // every keystroke in the form.
  const coaExpense = useMemo(() => coa.filter((a) => a.type === "Expense"), [coa]);

  function blank(kind = "inventory") {
    const today = format(new Date(), "yyyy-MM-dd");
    return {
      vendor_id: "", date: today,
      gl_posting_date: today,
      due_date: format(new Date(Date.now() + 14 * 864e5), "yyyy-MM-dd"),
      reference_number: "", description: "",
      terms: "net30", payment_priority: "normal", on_hold: false,
      lines: [withRid({ product_id: "", description: "", quantity: 1, unit_price: 0, tax_rate: 0,
                         coa_code: "", department: "", bank_account_id: "",
                         memo: "", is_1099: false })],
      notes: "",
      currency: "USD",
      kind, // iter 72ai — "inventory" | "service"
    };
  }
  const load = useCallback(async () => {
    try {
      const [{ data: b }, { data: v }, { data: p }, { data: c }, { data: d }, { data: w }] = await Promise.all([
        api.get("/bills"), api.get("/vendors"), api.get("/products"),
        api.get("/coa"), api.get("/categories"), api.get("/accounts"),
      ]);
      setRows(b); setVendors(v); setProducts(p || []);
      setCoa((c || []).filter((x) => x.is_active !== false));
      // iter 72ah — `/categories` returns EITHER an array OR a grouped
      // dict on Business ({expense:[], income:[], saving:[]}); flatten
      // so `.map()` works either way.
      const flatDepts = Array.isArray(d)
        ? d
        : Object.values(d || {}).flat();
      setDepartments(flatDepts);
      setWallets(w || []);
    } catch (err) {
      console.warn("[Payables] load bills failed:", err?.message);
      toast.error("Couldn't load bills");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // iter 72ao — load the entity's `bill_approval_required` setting so the
  // page can show the toggle and the Pending Approvals filter. We derive
  // the current entity from `/entities` + `x-entity-id` cookie by asking
  // the backend for /entities and matching by name — but the simplest way
  // is to just read from the `/user` endpoint's `entities` list. Since
  // this page mounts under an active workspace, /entities reliably returns
  // the current one alongside others; we filter by the header set by the
  // API client.
  const [currentEntityId, setCurrentEntityId] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        // Pull entities list + auth "me" to find the active workspace id.
        const [{ data: me }, { data: ents }] = await Promise.all([
          api.get("/auth/me"), api.get("/entities"),
        ]);
        // The API client attaches x-entity-id from cookies/session. To learn
        // which entity that resolves to server-side we hit /entities/current
        // if it exists; otherwise fall back to reading the "activeEntityId"
        // localStorage key which the EntityContext already sets.
        const activeId =
          (typeof window !== "undefined"
            && localStorage.getItem("activeEntityId"))
          || (me?.default_entity_id) || (ents[0]?.id);
        if (!activeId) return;
        setCurrentEntityId(activeId);
        const match = ents.find((e) => e.id === activeId);
        if (match) setApprovalRequired(!!match.bill_approval_required);
      } catch (err) {
        // Non-fatal — page still works without the toggle.
      }
    })();
  }, []);

  const toggleApprovalRequired = async (next) => {
    if (!currentEntityId) return;
    try {
      const { data } = await api.patch(`/entities/${currentEntityId}`,
                                          { bill_approval_required: next });
      setApprovalRequired(!!data.bill_approval_required);
      toast.success(next
        ? "New bills will now require approval before posting"
        : "Approval requirement turned off");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update setting");
    }
  };

  const submitForApproval = async (bill) => {
    try {
      await api.post(`/bills/${bill.id}/submit`);
      toast.success("Submitted for approval");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit");
    }
  };
  const approveBill = async (bill) => {
    try {
      await api.post(`/bills/${bill.id}/approve`);
      toast.success("Bill approved");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't approve");
    }
  };
  const rejectBill = async () => {
    if (!rejectingBill) return;
    try {
      await api.post(`/bills/${rejectingBill.id}/reject`,
                       { reason: rejectReason || null });
      toast.success("Bill rejected");
      setRejectingBill(null);
      setRejectReason("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reject");
    }
  };

  // Filter rows by approval state when the top-tab filter is engaged.
  const filteredRows = approvalFilter === "pending"
    ? rows.filter((r) => r.approval_status === "pending_approval")
    : approvalFilter === "rejected"
      ? rows.filter((r) => r.approval_status === "rejected")
      : rows;
  const pendingCount = rows.filter((r) => r.approval_status === "pending_approval").length;
  const rejectedCount = rows.filter((r) => r.approval_status === "rejected").length;

  // Iter 70 — picking a product auto-fills description + unit_price (buy cost).
  // The product_id triggers stock receipt when the bill's journal is booked.
  const pickProduct = (i, pid) => {
    const p = products.find((x) => x.id === pid);
    const n = [...form.lines];
    n[i] = {
      ...n[i],
      product_id: pid || "",
      description: p ? p.name : n[i].description,
      unit_price: p ? p.unit_cost : n[i].unit_price,
    };
    setForm({ ...form, lines: n });
  };

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    for (const l of form.lines) {
      const s = (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0);
      sub += s; tax += s * (parseFloat(l.tax_rate) || 0) / 100;
    }
    return { subtotal: sub, tax_total: tax, total: sub + tax };
  }, [form.lines]);

  const save = async ({ postAndNew = false } = {}) => {
    if (!form.vendor_id) return toast.error("Pick a vendor");
    const payload = {
      vendor_id: form.vendor_id,
      date: form.date, due_date: form.due_date,
      gl_posting_date: form.gl_posting_date || null,
      reference_number: form.reference_number || null,
      description: form.description || null,
      terms: form.terms || null,
      payment_priority: form.payment_priority || "normal",
      on_hold: !!form.on_hold,
      lines: form.lines.filter((l) => l.description).map((l) => ({
        product_id: l.product_id || null,
        description: l.description,
        quantity: parseFloat(l.quantity) || 0,
        unit_price: parseFloat(l.unit_price) || 0,
        tax_rate: parseFloat(l.tax_rate) || 0,
        coa_code: l.coa_code || null,
        department: l.department || null,
        bank_account_id: l.bank_account_id || null,
        memo: l.memo || null,
        is_1099: !!l.is_1099,
      })),
      notes: form.notes,
      currency: form.currency || "USD",
      kind: form.kind || "inventory",
    };
    if (payload.lines.length === 0) return toast.error("Add at least one line");
    try {
      if (editing) await api.patch(`/bills/${editing.id}`, payload);
      else await api.post("/bills", payload);
      toast.success("Saved"); load();
      if (postAndNew) {
        setEditing(null);
        setForm(blank(form.kind));
      } else {
        setOpen(false);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  // iter 72an — Terms → Due date auto-compute. Runs whenever terms or the
  // bill date change. `custom` keeps whatever the user picked manually.
  const applyTerms = (t, baseDate) => {
    if (!t || t === "custom") return null;
    const d = new Date(baseDate);
    if (isNaN(d)) return null;
    const days = { due_on_receipt: 0, net10: 10, net15: 15, net30: 30,
                   net45: 45, net60: 60, net90: 90 }[t];
    if (t === "eom") {
      const eom = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      return format(eom, "yyyy-MM-dd");
    }
    if (days == null) return null;
    return format(new Date(d.getTime() + days * 864e5), "yyyy-MM-dd");
  };
  const setTerms = (t) => {
    const newDue = applyTerms(t, form.date);
    setForm({ ...form, terms: t, ...(newDue ? { due_date: newDue } : {}) });
  };
  const setDate = (d) => {
    const newDue = applyTerms(form.terms, d);
    setForm({ ...form, date: d, ...(newDue ? { due_date: newDue } : {}) });
  };

  // iter 72an — Populate lines/terms from the vendor's most recent bill.
  const populateFromLast = async () => {
    if (!form.vendor_id) return toast.error("Pick a vendor first");
    setPopulating(true);
    try {
      const { data } = await api.get("/bills/populate-last",
                                       { params: { vendor_id: form.vendor_id } });
      setForm({
        ...form,
        terms: data.terms || form.terms,
        payment_priority: data.payment_priority || form.payment_priority,
        description: data.description || form.description,
        currency: data.currency || form.currency,
        kind: data.kind || form.kind,
        lines: (data.lines || []).map((l) => withRid({ ...l, is_1099: !!l.is_1099 })),
      });
      toast.success("Populated from last bill");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No prior bill for this vendor");
    } finally {
      setPopulating(false);
    }
  };

  const recordPayment = async () => {
    const amount = parseFloat(pay.amount);
    if (!amount || amount <= 0) return toast.error("Invalid amount");
    try {
      const { data } = await api.post(`/bills/${payOpen.id}/payments`, { ...pay, amount });
      toast.success("Payment recorded");
      setPayOpen({ ...data, balance: data.balance });
      setPay({ amount: "", date: format(new Date(), "yyyy-MM-dd"), method: "bank", memo: "", account_id: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const confirmVoid = async () => {
    if (!voidTarget) return;
    const reason = voidReason.trim();
    if (reason.length < 3) return toast.error("Reason must be at least 3 characters");
    try {
      const { data } = await api.post(
        `/bills/${payOpen.id}/payments/${voidTarget.paymentId}/void`,
        { reason },
      );
      toast.success("Payment voided");
      setPayOpen({ ...data, balance: data.balance });
      setVoidTarget(null); setVoidReason("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Void failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this bill?")) return;
    try { await api.delete(`/bills/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4">
      {/* iter 72ao — Approval workflow strip: toggle + filter tabs. */}
      <div className="flex items-center justify-between gap-4 flex-wrap border-b border-[var(--divider)] pb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button"
                   onClick={() => setApprovalFilter("all")}
                   data-testid="bill-filter-all"
                   className={`text-xs px-3 py-1.5 rounded-full transition ${
                     approvalFilter === "all"
                       ? "bg-[#2F4F4F] text-[#FDFCF8]"
                       : "text-[var(--muted-text)] hover:bg-[var(--hover)]"
                   }`}>
            All bills · {rows.length}
          </button>
          {(approvalRequired || pendingCount > 0) && (
            <button type="button"
                     onClick={() => setApprovalFilter("pending")}
                     data-testid="bill-filter-pending"
                     className={`text-xs px-3 py-1.5 rounded-full transition flex items-center gap-1.5 ${
                       approvalFilter === "pending"
                         ? "bg-[#B4823A] text-white"
                         : "text-[var(--muted-text)] hover:bg-[var(--hover)]"
                     }`}>
              Pending approval
              {pendingCount > 0 && (
                <span className={`text-[10px] font-semibold rounded-full px-1.5 ${
                  approvalFilter === "pending"
                    ? "bg-white/25"
                    : "bg-[#B4823A] text-white"
                }`}>{pendingCount}</span>
              )}
            </button>
          )}
          {rejectedCount > 0 && (
            <button type="button"
                     onClick={() => setApprovalFilter("rejected")}
                     data-testid="bill-filter-rejected"
                     className={`text-xs px-3 py-1.5 rounded-full transition flex items-center gap-1.5 ${
                       approvalFilter === "rejected"
                         ? "bg-[#B4283A] text-white"
                         : "text-[var(--muted-text)] hover:bg-[var(--hover)]"
                     }`}>
              Rejected
              <span className={`text-[10px] font-semibold rounded-full px-1.5 ${
                approvalFilter === "rejected"
                  ? "bg-white/25"
                  : "bg-[#B4283A] text-white"
              }`}>{rejectedCount}</span>
            </button>
          )}
        </div>
        <label className="flex items-center gap-2 text-xs text-[var(--muted-text)] cursor-pointer">
          <input type="checkbox" checked={approvalRequired}
                  onChange={(e) => toggleApprovalRequired(e.target.checked)}
                  data-testid="bill-approval-required-toggle"
                  className="h-4 w-4" />
          Require approval before posting bills
        </label>
      </div>
      <div className="flex justify-end gap-2">
        {/* iter 72ai — service bill button (no product picker). */}
        <Button data-testid="bill-new-service-btn"
                 onClick={() => { setEditing(null); setForm(blank("service")); setOpen(true); }}
                 variant="outline" className="rounded-md h-10 px-4">
          <Sparkles className="w-4 h-4 mr-2" /> New service bill
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="bill-new-btn" onClick={() => { setEditing(null); setForm(blank("inventory")); setOpen(true); }}
              className="rounded-md btn-brand h-10 px-4">
              <Plus className="w-4 h-4 mr-2" /> New bill
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editing ? "Edit bill" : (form.kind === "service" ? "New service bill" : "New bill")}
                <span data-testid="bill-kind-badge"
                       className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                         form.kind === "service"
                           ? "bg-[#EAF4EE] text-[#2F6B4A]"
                           : "bg-[#F2ECE5] text-[#5B4B2E]"
                       }`}>
                  {form.kind === "service" ? "Service" : "Inventory"}
                </span>
                {form.on_hold && (
                  <span data-testid="bill-onhold-badge"
                         className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FCE7E9] text-[#B4283A]">
                    On hold
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            {/* Row 1 — dates + currency */}
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Date *</Label>
                <Input type="date" value={form.date} onChange={(e) => setDate(e.target.value)}
                        data-testid="bill-form-date" />
              </div>
              <div><Label>GL posting date</Label>
                <Input type="date" value={form.gl_posting_date || ""}
                        onChange={(e) => setForm({ ...form, gl_posting_date: e.target.value })}
                        data-testid="bill-form-gl-date" />
              </div>
              <div><Label>Currency</Label>
                <Select value={form.currency || "USD"} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="bill-form-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="NGN">NGN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Row 2 — vendor + numbers */}
            <div className="grid grid-cols-3 gap-3 mt-3">
              <div>
                <Label>Vendor *</Label>
                <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                  <SelectTrigger data-testid="bill-form-vendor"><SelectValue placeholder="Pick vendor" /></SelectTrigger>
                  <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" size="sm" variant="outline" className="mt-1 h-7 text-xs"
                         data-testid="bill-populate-last"
                         disabled={!form.vendor_id || populating}
                         onClick={populateFromLast}>
                  {populating ? "Populating…" : "Populate from last bill"}
                </Button>
              </div>
              <div>
                <Label>Bill number</Label>
                <Input placeholder="Auto-generated"
                        value={form.number || ""}
                        onChange={(e) => setForm({ ...form, number: e.target.value })}
                        data-testid="bill-form-number" />
              </div>
              <div>
                <Label>Reference number</Label>
                <Input placeholder="Vendor's invoice #"
                        value={form.reference_number || ""}
                        onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
                        data-testid="bill-form-ref" />
              </div>
            </div>
            {/* Row 3 — description */}
            <div className="mt-3">
              <Label>Description</Label>
              <Input placeholder="Short header description (e.g. Q3 SaaS invoice)"
                      value={form.description || ""}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      data-testid="bill-form-description" />
            </div>
            {/* Row 4 — terms + due + priority + hold */}
            <div className="grid grid-cols-4 gap-3 mt-3 items-end">
              <div>
                <Label>Payment terms</Label>
                <Select value={form.terms || "custom"} onValueChange={setTerms}>
                  <SelectTrigger data-testid="bill-form-terms"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="due_on_receipt">Due on receipt</SelectItem>
                    <SelectItem value="net10">Net 10</SelectItem>
                    <SelectItem value="net15">Net 15</SelectItem>
                    <SelectItem value="net30">Net 30</SelectItem>
                    <SelectItem value="net45">Net 45</SelectItem>
                    <SelectItem value="net60">Net 60</SelectItem>
                    <SelectItem value="net90">Net 90</SelectItem>
                    <SelectItem value="eom">End of month</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due date *</Label>
                <Input type="date" value={form.due_date}
                        onChange={(e) => setForm({ ...form, due_date: e.target.value, terms: "custom" })}
                        data-testid="bill-form-due" />
              </div>
              <div>
                <Label>Payment priority</Label>
                <Select value={form.payment_priority || "normal"}
                         onValueChange={(v) => setForm({ ...form, payment_priority: v })}>
                  <SelectTrigger data-testid="bill-form-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 h-9">
                <input type="checkbox" checked={!!form.on_hold}
                        onChange={(e) => setForm({ ...form, on_hold: e.target.checked })}
                        id="bill-hold" data-testid="bill-form-hold"
                        className="h-4 w-4" />
                <label htmlFor="bill-hold" className="text-sm">Place this bill on hold</label>
              </div>
            </div>
            {/* Attachment placeholder — parity with the Sage layout. Real
                file-upload wiring needs the Emergent Object Storage playbook. */}
            <div className="mt-3 text-xs text-[var(--muted-text)] italic"
                  data-testid="bill-form-attachment-placeholder">
              Attachment: coming soon (needs cloud file storage setup)
            </div>

            <div className="mt-4 border-t border-[var(--divider)] pt-3">
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] mb-2">Entries</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--divider)]">
                  {form.kind !== "service" && (<th className="py-2 text-left w-40">Product</th>)}
                  <th className="py-2 text-left">Description</th>
                  <th className="w-16 text-center">1099</th>
                  <th className="w-16 text-right">Qty</th>
                  <th className="w-20 text-right">Unit</th>
                  <th className="w-14 text-right">Tax%</th>
                  <th className="py-2 text-left w-36">Memo</th>
                  <th className="w-24 text-right">Line</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {form.lines.map((l, i) => {
                  const line = (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0);
                  const t = line + line * (parseFloat(l.tax_rate) || 0) / 100;
                  return (
                    <Fragment key={l._rid}>
                    <tr className="border-b border-[var(--divider)]">
                      {form.kind !== "service" && (
                      <td className="py-2 pr-2">
                        <Select value={l.product_id || "none"} onValueChange={(v) => pickProduct(i, v === "none" ? "" : v)}>
                          <SelectTrigger className="h-9 text-xs" data-testid={`bill-line-${i}-product`}>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— none —</SelectItem>
                            {products.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name}{p.sku ? ` · ${p.sku}` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      )}
                      <td className="py-2 pr-2"><Input value={l.description} onChange={(e) => { const n = [...form.lines]; n[i].description = e.target.value; setForm({ ...form, lines: n }); }} className="h-9" data-testid={`bill-line-${i}-desc`} /></td>
                      <td className="py-2 pr-2 text-center">
                        <input type="checkbox" checked={!!l.is_1099}
                                onChange={(e) => { const n = [...form.lines]; n[i].is_1099 = e.target.checked; setForm({ ...form, lines: n }); }}
                                className="h-4 w-4" data-testid={`bill-line-${i}-1099`} />
                      </td>
                      <td className="py-2 pr-2"><Input type="number" value={l.quantity} onChange={(e) => { const n = [...form.lines]; n[i].quantity = e.target.value; setForm({ ...form, lines: n }); }} className="h-9 text-right" /></td>
                      <td className="py-2 pr-2"><Input type="number" step="0.01" value={l.unit_price} onChange={(e) => { const n = [...form.lines]; n[i].unit_price = e.target.value; setForm({ ...form, lines: n }); }} className="h-9 text-right" /></td>
                      <td className="py-2 pr-2"><Input type="number" step="0.01" value={l.tax_rate} onChange={(e) => { const n = [...form.lines]; n[i].tax_rate = e.target.value; setForm({ ...form, lines: n }); }} className="h-9 text-right" /></td>
                      <td className="py-2 pr-2"><Input placeholder="Line memo" value={l.memo || ""} onChange={(e) => { const n = [...form.lines]; n[i].memo = e.target.value; setForm({ ...form, lines: n }); }} className="h-9" data-testid={`bill-line-${i}-memo`} /></td>
                      <td className="py-2 pr-2 text-right font-mono">{t.toFixed(2)}</td>
                      <td>{form.lines.length > 1 && <Button size="icon" variant="ghost" onClick={() => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) })}><X className="w-4 h-4" /></Button>}</td>
                    </tr>
                    {/* iter 72ah — per-line CoA (Expense), Department, and
                        Bank pickers. Bank is metadata only for later
                        settlement — payment recording stays separate. */}
                    <tr className="border-b border-[var(--divider)] bg-[var(--hover)]/40" data-testid={`bill-line-${i}-tags`}>
                      <td colSpan={form.kind === "service" ? 8 : 9} className="pt-1 pb-2 px-2">
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] mb-1">CoA account (Expense)</div>
                            <Select value={l.coa_code || "none"}
                                     onValueChange={(v) => { const n = [...form.lines]; n[i].coa_code = v === "none" ? "" : v; setForm({ ...form, lines: n }); }}>
                              <SelectTrigger className="h-8 text-xs" data-testid={`bill-line-${i}-coa`}>
                                <SelectValue placeholder="— default (6900 Other) —" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">— default (6900 Other) —</SelectItem>
                                {coaExpense.map((a) => (
                                  <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] mb-1">Department</div>
                            <Select value={l.department || "none"}
                                     onValueChange={(v) => { const n = [...form.lines]; n[i].department = v === "none" ? "" : v; setForm({ ...form, lines: n }); }}>
                              <SelectTrigger className="h-8 text-xs" data-testid={`bill-line-${i}-dept`}>
                                <SelectValue placeholder="— none —" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">— none —</SelectItem>
                                {departments.map((d) => (
                                  <SelectItem key={d.id || d.name} value={d.name}>{d.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] mb-1">Settle from bank (later)</div>
                            <Select value={l.bank_account_id || "none"}
                                     onValueChange={(v) => { const n = [...form.lines]; n[i].bank_account_id = v === "none" ? "" : v; setForm({ ...form, lines: n }); }}>
                              <SelectTrigger className="h-8 text-xs" data-testid={`bill-line-${i}-bank`}>
                                <SelectValue placeholder="— pick later —" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">— pick later —</SelectItem>
                                {wallets.filter((w) => (w.currency || "USD") === (form.currency || "USD")).map((w) => (
                                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </td>
                    </tr>
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr><td colSpan={form.kind === "service" ? 8 : 9}><Button size="sm" variant="ghost" onClick={() => setForm({ ...form, lines: [...form.lines, withRid({ product_id: "", description: "", quantity: 1, unit_price: 0, tax_rate: 0, coa_code: "", department: "", bank_account_id: "", memo: "", is_1099: false })] })}><Plus className="w-3.5 h-3.5 mr-1" /> Add line</Button></td></tr>
                <tr><td colSpan={form.kind === "service" ? 6 : 7} className="text-right text-xs text-[var(--muted-text)]">Subtotal</td><td className="text-right font-mono">{totals.subtotal.toFixed(2)}</td><td /></tr>
                <tr><td colSpan={form.kind === "service" ? 6 : 7} className="text-right text-xs text-[var(--muted-text)]">Tax</td><td className="text-right font-mono">{totals.tax_total.toFixed(2)}</td><td /></tr>
                <tr><td colSpan={form.kind === "service" ? 6 : 7} className="text-right text-sm font-semibold">Total</td><td data-testid="bill-total" className="text-right font-mono font-semibold border-t border-[#2F4F4F]">{totals.total.toFixed(2)}</td><td /></tr>
              </tfoot>
            </table>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button variant="outline" data-testid="bill-form-post-and-new"
                       onClick={() => save({ postAndNew: true })}
                       className="rounded-full">
                Save & new
              </Button>
              <Button data-testid="bill-form-save" onClick={() => save()} className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]">{editing ? "Save" : "Create"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-flat overflow-hidden">
        {(() => { const visible = filteredRows.filter((b) => isActive(b.currency || "USD")); return visible.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--muted-text)]">
            {rows.length === 0 ? "No bills yet." : (
              approvalFilter === "pending" ? "No bills waiting for approval."
                : approvalFilter === "rejected" ? "No rejected bills."
                : "No bills on the selected currency rail."
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--hover)] text-xs uppercase tracking-wider text-[var(--muted-text)]">
              <tr><th className="text-left px-5 py-2.5">Number</th><th className="text-left px-4 py-2.5">Vendor</th><th className="text-left px-4 py-2.5">Due</th><th className="text-left px-4 py-2.5">Ccy</th><th className="text-right px-4 py-2.5">Total</th><th className="text-right px-4 py-2.5">Balance</th><th className="px-4 py-2.5">Approval</th><th className="px-4 py-2.5">Status</th><th className="w-40" /></tr>
            </thead>
            <tbody>
              {visible.map((b) => (
                <tr key={b.id} data-testid={`bill-row-${b.id}`} className="border-b border-[var(--divider)] last:border-none">
                  <td className="px-5 py-3 font-mono text-xs">{b.number}</td>
                  <td className="px-4 py-3">{b.vendor_name}</td>
                  <td className="px-4 py-3 text-[var(--muted-text)]">{b.due_date}</td>
                  <td className="px-4 py-3 font-mono text-[10px]"><span className="px-1.5 py-0.5 rounded bg-[var(--hover)]">{b.currency || "USD"}</span></td>
                  <td className="px-4 py-3 text-right font-mono">{fm(b.total, b.currency)}</td>
                  <td className="px-4 py-3 text-right font-mono">{fm(b.balance, b.currency)}</td>
                  <td className="px-4 py-3">
                    {(() => {
                      const a = b.approval_status || "approved";
                      const cls = a === "approved"
                        ? "bg-[#EAF4EE] text-[#2F6B4A]"
                        : a === "pending_approval"
                          ? "bg-[#FFF3D6] text-[#8A6100]"
                          : a === "rejected"
                            ? "bg-[#FCE7E9] text-[#B4283A]"
                            : "bg-[var(--hover)] text-[var(--muted-text)]";
                      const label = a === "pending_approval" ? "Pending" : a;
                      return <span data-testid={`bill-approval-${b.id}`}
                                     className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${cls}`}
                                     title={b.rejection_reason || ""}>{label}</span>;
                    })()}
                  </td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${STATUS_STYLES[b.status]}`}>{b.status}</span></td>
                  <td className="px-4 py-3 text-right">
                    {/* iter 72ao — approval actions before payment/post options. */}
                    {b.approval_status === "pending_approval" && (
                      <>
                        <Button size="icon" variant="ghost" title="Approve"
                                 data-testid={`bill-approve-${b.id}`}
                                 onClick={() => approveBill(b)}
                                 className="text-[#2F6B4A] hover:bg-[#EAF4EE]">
                          <BookOpenCheck className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" title="Reject"
                                 data-testid={`bill-reject-${b.id}`}
                                 onClick={() => { setRejectingBill(b); setRejectReason(""); }}
                                 className="text-[#B4283A] hover:bg-[#FCE7E9]">
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {b.approval_status === "rejected" && (
                      <Button size="sm" variant="outline"
                               data-testid={`bill-resubmit-${b.id}`}
                               onClick={() => submitForApproval(b)}
                               className="h-8 px-2 text-xs text-[#8A6100]">
                        Resubmit
                      </Button>
                    )}
                    {b.status !== "paid" && (
                      <Button size="icon" variant="ghost" data-testid={`bill-pay-${b.id}`}
                               onClick={() => {
                                 const firstBank = (b.lines || []).find((l) => l.bank_account_id)?.bank_account_id;
                                 setPay({
                                   amount: String(b.balance),
                                   date: format(new Date(), "yyyy-MM-dd"),
                                   method: "bank",
                                   memo: "",
                                   account_id: firstBank || "",
                                 });
                                 setPayOpen(b);
                               }}
                               title="Record payment">
                        <CreditCard className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" data-testid={`bill-post-${b.id}`} title={b.posted_journal_id ? "Already posted — use Reverse to unpost" : "Post to journal"} disabled={!!b.posted_journal_id} onClick={async () => {
                      try {
                        const { data } = await api.post(`/bills/${b.id}/post-journal`);
                        toast.success(data.already_posted ? "Already posted to journal" : "Journal entry posted");
                        load();
                      } catch (err) { toast.error(err.response?.data?.detail || "Post failed"); }
                    }}>
                      <BookOpenCheck className="w-4 h-4" />
                    </Button>
                    {b.posted_journal_id && (
                      <Button size="icon" variant="ghost" data-testid={`bill-reverse-${b.id}`} title="Reverse posted journal entry" onClick={() => setReverseTarget(b)}>
                        <Undo2 className="w-4 h-4 text-[#B4283A]" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => { setEditing(b); setForm({ vendor_id: b.vendor_id, date: b.date, due_date: b.due_date, lines: b.lines.map((l) => withRid(l)), notes: b.notes || "", currency: b.currency || "USD" }); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(b.id)}><Trash2 className="w-4 h-4 text-[#E63946]" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ); })()}
      </div>

      <Dialog open={!!payOpen} onOpenChange={(v) => !v && setPayOpen(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Record payment</DialogTitle><DialogDescription>Bill {payOpen?.number} · balance {fm(payOpen?.balance, payOpen?.currency)}</DialogDescription></DialogHeader>

          {/* Iter 72 — existing bill payments with void action */}
          {(payOpen?.payments || []).length > 0 && (
            <div className="border border-[var(--divider)] rounded-md" data-testid="bill-payments-list">
              <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-[var(--muted-text)] border-b border-[var(--divider)]">
                Recorded payments
              </div>
              <ul className="divide-y divide-[var(--divider)]">
                {(payOpen.payments || []).map((p) => (
                  <li key={p.id} className="flex items-center justify-between px-3 py-2 text-sm"
                      data-testid={`bill-payment-row-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-data">{fm(p.amount, payOpen.currency)} <span className="text-[var(--muted-text)] text-xs">· {p.date} · {p.method}</span></div>
                      {p.memo && <div className="text-xs text-[var(--muted-text)] truncate">{p.memo}</div>}
                    </div>
                    {p.method === "credit_memo" ? (
                      <span className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] px-2">memo</span>
                    ) : (
                      <Button size="sm" variant="ghost"
                        data-testid={`bill-payment-void-${p.id}`}
                        className="text-[#B4283A] hover:bg-[#FCE7E9]"
                        onClick={() => { setVoidTarget({ paymentId: p.id, amount: p.amount, date: p.date }); setVoidReason(""); }}>
                        <Undo2 className="w-3.5 h-3.5 mr-1" /> Reverse payment
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
              {(payOpen?.voided_payments || []).length > 0 && (
                <div className="px-3 py-2 border-t border-[var(--divider)] bg-[var(--hover)]">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-1">
                    Voided ({payOpen.voided_payments.length})
                  </div>
                  <ul className="space-y-1">
                    {payOpen.voided_payments.map((p) => (
                      <li key={p.id} className="text-xs text-[var(--muted-text)] line-through"
                          data-testid={`bill-payment-voided-${p.id}`}>
                        {fm(p.amount, payOpen.currency)} · {p.date} · {p.voided_reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount</Label><Input type="number" step="0.01" data-testid="bill-payment-amount" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} /></div>
            <div><Label>Date</Label><Input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} /></div>
            <div className="col-span-2"><Label>Method</Label>
              <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank">Bank transfer</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* iter 72ai — pre-fill the bank from the bill line. */}
            <div className="col-span-2">
              <Label>Pay from bank {pay.account_id && <span className="text-[10px] text-[#2F6B4A] ml-2">(auto-filled from line)</span>}</Label>
              <Select value={pay.account_id || "none"}
                       onValueChange={(v) => setPay({ ...pay, account_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="bill-payment-bank"><SelectValue placeholder="— none —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— none —</SelectItem>
                  {wallets.filter((w) => (w.currency || "USD") === (payOpen?.currency || "USD")).map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Memo</Label><Input value={pay.memo} onChange={(e) => setPay({ ...pay, memo: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setPayOpen(null)}>Close</Button>
            <Button data-testid="bill-payment-save" onClick={recordPayment} className="rounded-full bg-[#2F6B4A] hover:bg-[#245536] text-white">Record</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter 72 — Void (aka Reverse Payment) bill payment confirmation */}
      <Dialog open={!!voidTarget} onOpenChange={(v) => { if (!v) { setVoidTarget(null); setVoidReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="bill-payment-void-dialog">
          <DialogHeader>
            <DialogTitle>Reverse payment</DialogTitle>
            <DialogDescription>
              Reversing {voidTarget && fm(voidTarget.amount, payOpen?.currency)} recorded on {voidTarget?.date} will undo its cash impact and keep an audit record on this bill. This action is preserved in the audit log — it is not a delete.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Reason <span className="text-[#B4283A]">*</span></Label>
            <Textarea data-testid="bill-payment-void-reason" value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g. Duplicate payment / wrong vendor / amount corrected"
              rows={3} />
            <p className="text-xs text-[var(--muted-text)]">Minimum 3 characters. Stored on the bill audit trail.</p>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="ghost" onClick={() => { setVoidTarget(null); setVoidReason(""); }}>Cancel</Button>
            <Button data-testid="bill-payment-void-confirm"
              className="bg-[#B4283A] hover:bg-[#8f1f2d] text-white" onClick={confirmVoid}>
              Reverse payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reverseTarget} onOpenChange={(v) => { if (!v) { setReverseTarget(null); setReverseReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="bill-reverse-dialog">
          <DialogHeader>
            <DialogTitle>Reverse journal entry</DialogTitle>
            <DialogDescription>
              This will post an offsetting journal entry for bill {reverseTarget?.number} ({fm(reverseTarget?.total)}) and flip it back to <span className="font-semibold">unposted</span> so it can be re-posted after correction. The original journal entry remains in the ledger for audit — this is not a delete.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Reason (optional but recommended)</Label>
            <Textarea data-testid="bill-reverse-reason" value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} placeholder="e.g. Wrong vendor / duplicate / amount corrected" rows={3} />
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="ghost" onClick={() => { setReverseTarget(null); setReverseReason(""); }}>Cancel</Button>
            <Button data-testid="bill-reverse-confirm" className="bg-[#B4283A] hover:bg-[#8f1f2d] text-white" onClick={async () => {
              try {
                const { data } = await api.post(`/bills/${reverseTarget.id}/reverse-journal`, { reason: reverseReason });
                toast.success(`Reversed · offsetting JE created (${data.reversal_je_id.slice(-6)})`);
                setReverseTarget(null); setReverseReason("");
                load();
              } catch (err) {
                toast.error(err.response?.data?.detail || "Reversal failed");
              }
            }}>Reverse posting</Button>
          </div>
        </DialogContent>
      </Dialog>
      <_RejectDialog rejectingBill={rejectingBill}
                       setRejectingBill={setRejectingBill}
                       rejectReason={rejectReason}
                       setRejectReason={setRejectReason}
                       rejectBill={rejectBill} />
    </div>
  );
}
